// 地圖供應商（plan/vms-web-v0.md §3.1 MapProvider、§5.2.2）：Google Maps JS / 百度地圖 JS API GL 包在同一個介面後面。
//
// 介面進出一律 WGS-84；轉 GCJ-02 / BD-09 在這裡（lib/geo）。
// 標記、扇形、平面圖疊圖都畫在我們自己的一層 overlay 裡（兩家各給一個跟著地圖走的 pane 與投影函式），
// 所以兩家地圖上疊出來的位置一致（FP-02 驗收），供應商相關的程式只有載入、投影、事件三件事。
//
// key 在品牌設定（/config.json maps.*，來自 brands/<Brand>/vms/vms.yaml）；沒 key 的供應商不載入。
// Google 3 秒內載不起來 → 呼叫端改用百度並提示（§5.2.2）。

import { hasDevFeatures, type MapProvider } from './appConfig';
import { fromDisplay, toDisplay, type LatLng } from './geo';

export const GOOGLE_LOAD_TIMEOUT_MS = 3000;

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: any;
    BMapGL?: any;
    __kinioMapCb?: Record<string, () => void>;
  }
}

const loading: Partial<Record<MapProvider, Promise<void>>> = {};

function loadScript(src: string, cbName: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    window.__kinioMapCb = window.__kinioMapCb ?? {};
    const timer = setTimeout(() => reject(new Error('MAP_LOAD_TIMEOUT')), timeoutMs);
    window.__kinioMapCb[cbName] = () => {
      clearTimeout(timer);
      resolve();
    };
    const s = document.createElement('script');
    s.src = src.replace('__CB__', `__kinioMapCb.${cbName}`);
    s.async = true;
    s.onerror = () => {
      clearTimeout(timer);
      reject(new Error('MAP_LOAD_FAILED'));
    };
    document.head.appendChild(s);
  });
}

export interface MapKeys {
  google?: { browser_key?: string; map_id?: string };
  baidu?: { ak?: string };
  /** dev / test 的開發底圖（/config.json maps.dev_tiles；沒有 = DEFAULT_DEV_TILES） */
  dev_tiles?: DevTiles;
}

export function hasKey(keys: MapKeys, p: MapProvider): boolean {
  return p === 'google' ? !!keys.google?.browser_key : !!keys.baidu?.ak;
}

export function loadProvider(p: MapProvider, keys: MapKeys, lang = 'zh-TW'): Promise<void> {
  if (p === 'google' && window.google?.maps?.OverlayView) return Promise.resolve();
  if (p === 'baidu' && window.BMapGL?.Map) return Promise.resolve();
  if (!hasKey(keys, p)) return Promise.reject(new Error('MAP_NO_KEY'));
  if (!loading[p]) {
    loading[p] =
      p === 'google'
        ? loadScript(
            `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(keys.google!.browser_key!)}&v=weekly&language=${encodeURIComponent(lang)}&callback=__CB__&loading=async`,
            'google',
            GOOGLE_LOAD_TIMEOUT_MS,
          )
        : loadScript(`https://api.map.baidu.com/api?type=webgl&v=1.0&ak=${encodeURIComponent(keys.baidu!.ak!)}&callback=__CB__`, 'baidu', 10_000);
    loading[p]!.catch(() => delete loading[p]);
  }
  return loading[p]!;
}

// ---- 畫在 overlay 的東西（WGS-84） ----

export interface MarkerSpec {
  id: string;
  pos: LatLng;
  /** 標記內容（emoji / 數字） */
  glyph: string;
  label?: string;
  cls?: string;
  title?: string;
  /** 參與聚合（MAP-01） */
  cluster?: boolean;
  onClick?: () => void;
}

export interface PolygonSpec {
  id: string;
  path: LatLng[];
  cls?: string;
}

export interface ImageSpec {
  id: string;
  url: string;
  width: number;
  height: number;
  /** 圖的左上、右上、左下角（WGS-84） */
  tl: LatLng;
  tr: LatLng;
  bl: LatLng;
  opacity: number;
}

export interface MapLayers {
  markers: MarkerSpec[];
  polygons: PolygonSpec[];
  images: ImageSpec[];
}

/** dev：兩家都沒 key 時的開發用底圖（OSM 圖磚，WGS-84；只在 env=dev 用，產品不會出現）。 */
export type MapEngine = MapProvider | 'dev';

export interface MapAdapter {
  provider: MapEngine;
  setView(center: LatLng, zoom: number): void;
  fit(points: LatLng[]): void;
  getZoom(): number;
  getCenter(): LatLng;
  setLayers(l: MapLayers): void;
  onClick(cb: ((p: LatLng) => void) | null): void;
  /** 縮放級距改變（決定要不要畫設備層） */
  onZoom(cb: ((z: number) => void) | null): void;
  /** 底圖圖磚讀取失敗（B-19：失敗的圖磚不顯示，畫面改提示〔改用平面圖〕）；只有開發底圖會回報 */
  onTileError?(cb: (() => void) | null): void;
  destroy(): void;
}

interface ProviderShim {
  /** WGS-84 → pane 內像素 */
  project(p: LatLng): { x: number; y: number } | null;
  pane(): HTMLElement | null;
}

/** 我們自己的 overlay：標記（含聚合）、SVG 多邊形、仿射變形的平面圖。 */
class OverlayLayer {
  private root = document.createElement('div');
  private layers: MapLayers = { markers: [], polygons: [], images: [] };
  private imgEls = new Map<string, HTMLImageElement>();
  constructor(
    private shim: ProviderShim,
    private zoomIn: (at: LatLng) => void,
  ) {
    this.root.className = 'kmap-layer';
  }
  attach() {
    const pane = this.shim.pane();
    if (pane && this.root.parentElement !== pane) pane.appendChild(this.root);
  }
  set(l: MapLayers) {
    this.layers = l;
    this.draw();
  }
  detach() {
    this.root.remove();
  }
  draw() {
    this.attach();
    const { project } = this.shim;
    // 平面圖（最底）
    const keep = new Set<string>();
    for (const im of this.layers.images) {
      const tl = project(im.tl);
      const tr = project(im.tr);
      const bl = project(im.bl);
      if (!tl || !tr || !bl) continue;
      keep.add(im.id);
      let el = this.imgEls.get(im.id);
      if (!el) {
        el = document.createElement('img');
        el.className = 'kmap-image';
        el.draggable = false;
        this.imgEls.set(im.id, el);
        this.root.prepend(el);
      }
      if (el.src !== im.url) el.src = im.url;
      el.style.width = `${im.width}px`;
      el.style.height = `${im.height}px`;
      el.style.opacity = String(im.opacity);
      const a = (tr.x - tl.x) / im.width;
      const b = (tr.y - tl.y) / im.width;
      const c = (bl.x - tl.x) / im.height;
      const d = (bl.y - tl.y) / im.height;
      el.style.transform = `matrix(${a}, ${b}, ${c}, ${d}, ${tl.x}, ${tl.y})`;
    }
    for (const [id, el] of this.imgEls) {
      if (!keep.has(id)) {
        el.remove();
        this.imgEls.delete(id);
      }
    }
    // 多邊形與標記每次重畫（數量小，200 個場域 + 擺放）
    this.root.querySelectorAll('.kmap-svg, .kmap-marker').forEach((n) => n.remove());
    const polys = this.layers.polygons
      .map((p) => ({ p, pts: p.path.map(project).filter((x): x is { x: number; y: number } => !!x) }))
      .filter((x) => x.pts.length >= 3);
    if (polys.length) {
      const xs = polys.flatMap((x) => x.pts.map((q) => q.x));
      const ys = polys.flatMap((x) => x.pts.map((q) => q.y));
      const minX = Math.min(...xs) - 2;
      const minY = Math.min(...ys) - 2;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'kmap-svg');
      svg.style.left = `${minX}px`;
      svg.style.top = `${minY}px`;
      svg.setAttribute('width', String(Math.max(...xs) - minX + 4));
      svg.setAttribute('height', String(Math.max(...ys) - minY + 4));
      for (const { p, pts } of polys) {
        const el = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        el.setAttribute('points', pts.map((q) => `${q.x - minX},${q.y - minY}`).join(' '));
        el.setAttribute('class', p.cls ?? '');
        svg.appendChild(el);
      }
      this.root.appendChild(svg);
    }
    const placed = this.layers.markers.map((m) => ({ m, px: project(m.pos) })).filter((x) => !!x.px) as { m: MarkerSpec; px: { x: number; y: number } }[];
    for (const g of clusterPixels(placed, 44)) {
      const el = document.createElement('div');
      if (g.items.length > 1) {
        el.className = 'kmap-marker cluster ' + (g.items.some((i) => i.m.cls?.includes('alert')) ? 'alert' : g.items.some((i) => i.m.cls?.includes('warn')) ? 'warn' : '');
        el.innerHTML = `<span class="pin">${g.items.length}</span>`;
        el.title = g.items.map((i) => i.m.title ?? i.m.label ?? '').join('\n');
        const at = { lat: avg(g.items.map((i) => i.m.pos.lat)), lng: avg(g.items.map((i) => i.m.pos.lng)) };
        el.onclick = (e) => (e.stopPropagation(), this.zoomIn(at));
      } else {
        const m = g.items[0].m;
        el.className = 'kmap-marker ' + (m.cls ?? '');
        const pin = document.createElement('span');
        pin.className = 'pin';
        pin.textContent = m.glyph;
        el.appendChild(pin);
        if (m.label) {
          const lb = document.createElement('span');
          lb.className = 'label';
          lb.textContent = m.label;
          el.appendChild(lb);
        }
        if (m.title) el.title = m.title;
        if (m.onClick) el.onclick = (e) => (e.stopPropagation(), m.onClick!());
      }
      el.style.left = `${g.x}px`;
      el.style.top = `${g.y}px`;
      this.root.appendChild(el);
    }
  }
}

function avg(v: number[]): number {
  return v.reduce((a, b) => a + b, 0) / v.length;
}

/** 螢幕像素聚合：cluster=true 的標記在 radius 內併成一顆（MAP-01）；其他照原樣。 */
export function clusterPixels<T extends { m: { cluster?: boolean }; px: { x: number; y: number } }>(items: T[], radius: number): { x: number; y: number; items: T[] }[] {
  const out: { x: number; y: number; items: T[] }[] = [];
  for (const it of items) {
    if (it.m.cluster) {
      const g = out.find((c) => c.items[0].m.cluster && Math.hypot(c.x - it.px.x, c.y - it.px.y) < radius);
      if (g) {
        g.items.push(it);
        g.x = avg(g.items.map((i) => i.px.x));
        g.y = avg(g.items.map((i) => i.px.y));
        continue;
      }
    }
    out.push({ x: it.px.x, y: it.px.y, items: [it] });
  }
  return out;
}

// ---- Google ----

function googleAdapter(el: HTMLElement, center: LatLng, zoom: number, keys: MapKeys): MapAdapter {
  const g = window.google.maps;
  const disp = (p: LatLng) => toDisplay(p, 'google');
  const map = new g.Map(el, {
    center: disp(center),
    zoom,
    mapId: keys.google?.map_id || undefined,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: true,
    clickableIcons: false,
  });
  // eslint-disable-next-line prefer-const -- 刻意：Ov 的方法閉包要先看得到 layer，ov 建好後才指派
  let layer: OverlayLayer;
  class Ov extends g.OverlayView {
    onAdd() {
      layer.attach();
    }
    draw() {
      layer.draw();
    }
    onRemove() {
      layer.detach();
    }
  }
  const ov = new Ov();
  layer = new OverlayLayer(
    {
      project: (p) => {
        const proj = ov.getProjection();
        if (!proj) return null;
        const px = proj.fromLatLngToDivPixel(new g.LatLng(disp(p).lat, disp(p).lng));
        return px ? { x: px.x, y: px.y } : null;
      },
      pane: () => ov.getPanes()?.overlayMouseTarget ?? null,
    },
    (at) => {
      map.setCenter(disp(at));
      map.setZoom(Math.min((map.getZoom() ?? 10) + 3, 21));
    },
  );
  ov.setMap(map);
  let clickL: any = null;
  let zoomL: any = null;
  return {
    provider: 'google',
    onZoom: (cb) => {
      zoomL?.remove();
      zoomL = cb ? map.addListener('zoom_changed', () => cb(map.getZoom() ?? zoom)) : null;
    },
    setView: (c, z) => (map.setCenter(disp(c)), map.setZoom(z)),
    fit: (pts) => {
      if (!pts.length) return;
      if (pts.length === 1) return (map.setCenter(disp(pts[0])), map.setZoom(17));
      const b = new g.LatLngBounds();
      pts.forEach((p) => b.extend(disp(p)));
      map.fitBounds(b, 48);
    },
    getZoom: () => map.getZoom() ?? zoom,
    getCenter: () => {
      const c = map.getCenter();
      return fromDisplay({ lat: c.lat(), lng: c.lng() }, 'google');
    },
    setLayers: (l) => layer.set(l),
    onClick: (cb) => {
      clickL?.remove();
      clickL = cb ? map.addListener('click', (e: any) => cb(fromDisplay({ lat: e.latLng.lat(), lng: e.latLng.lng() }, 'google'))) : null;
    },
    destroy: () => {
      clickL?.remove();
      ov.setMap(null);
      el.innerHTML = '';
    },
  };
}

// ---- 百度（JS API GL） ----

function baiduAdapter(el: HTMLElement, center: LatLng, zoom: number): MapAdapter {
  const B = window.BMapGL;
  const disp = (p: LatLng) => toDisplay(p, 'baidu');
  const pt = (p: LatLng) => new B.Point(disp(p).lng, disp(p).lat);
  const map = new B.Map(el);
  map.centerAndZoom(pt(center), zoom);
  map.enableScrollWheelZoom(true);
  map.addControl(new B.ZoomControl());
  map.addControl(new B.ScaleControl());
  let layer: OverlayLayer | null = null;
  let paneEl: HTMLElement | null = null;
  // 自訂覆蓋物：initialize 回一個掛在 markerPane 的 div；draw 在地圖每次變動時被呼叫
  function Ov(this: any) {}
  Ov.prototype = new B.Overlay();
  Ov.prototype.initialize = function (m: any) {
    paneEl = document.createElement('div');
    paneEl.className = 'kmap-pane';
    m.getPanes().markerPane.appendChild(paneEl);
    return paneEl;
  };
  Ov.prototype.draw = function () {
    layer?.draw();
  };
  const ov = new (Ov as any)();
  layer = new OverlayLayer(
    {
      project: (p) => {
        const px = map.pointToOverlayPixel(pt(p));
        return px ? { x: px.x, y: px.y } : null;
      },
      pane: () => paneEl,
    },
    (at) => map.centerAndZoom(pt(at), Math.min(map.getZoom() + 3, 21)),
  );
  map.addOverlay(ov);
  const redraw = () => layer?.draw();
  map.addEventListener('update', redraw);
  map.addEventListener('zoomend', redraw);
  map.addEventListener('moveend', redraw);
  let zoomCb: ((z: number) => void) | null = null;
  map.addEventListener('zoomend', () => zoomCb?.(map.getZoom()));
  let clickCb: ((p: LatLng) => void) | null = null;
  map.addEventListener('click', (e: any) => {
    if (clickCb && e.latlng) clickCb(fromDisplay({ lat: e.latlng.lat, lng: e.latlng.lng }, 'baidu'));
  });
  return {
    provider: 'baidu',
    onZoom: (cb) => {
      zoomCb = cb;
    },
    setView: (c, z) => map.centerAndZoom(pt(c), z),
    fit: (pts) => {
      if (!pts.length) return;
      if (pts.length === 1) return map.centerAndZoom(pt(pts[0]), 18);
      map.setViewport(pts.map(pt), { margins: [48, 48, 48, 48] });
    },
    getZoom: () => map.getZoom(),
    getCenter: () => {
      const c = map.getCenter();
      return fromDisplay({ lat: c.lat, lng: c.lng }, 'baidu');
    },
    setLayers: (l) => layer?.set(l),
    onClick: (cb) => {
      clickCb = cb;
    },
    destroy: () => {
      map.removeOverlay(ov);
      map.destroy?.();
      el.innerHTML = '';
    },
  };
}

export async function createMap(p: MapEngine, el: HTMLElement, keys: MapKeys, center: LatLng, zoom: number, lang?: string): Promise<MapAdapter> {
  if (p === 'dev') return devAdapter(el, center, zoom, keys.dev_tiles?.url ? keys.dev_tiles : DEFAULT_DEV_TILES);
  await loadProvider(p, keys, lang);
  return p === 'google' ? googleAdapter(el, center, zoom, keys) : baiduAdapter(el, center, zoom);
}

// ---- 開發用底圖（Web Mercator 圖磚；拖曳平移、滾輪 / 按鈕縮放） ----
// SPEC #vms-web-290「dev / test 也必須有可用的底圖」（第二輪 B2-15）：圖磚設定從 vms 設定讀（/config.json maps.dev_tiles），
// 沒設時用 OpenStreetMap France 的 HOT 圖磚（不需要 key、使用規範允許少量開發用途）。CARTO basemaps 已改成要 key
// （沒有 key 回 200 的「API KEY REQUIRED」浮水印圖磚），不再當預設。直連 openstreetmap.org 官方圖磚不用（量大會被封）。
// 站台 Referrer-Policy 是 same-origin（不送 Referer），社群圖磚伺服器照使用規範會擋沒有 Referer 的請求 →
// 圖磚 <img> 帶 referrerpolicy="strict-origin-when-cross-origin"（只送來源網域）。
// 只給 dev / test（品牌沒有 Google / 百度 key 時）；讀取失敗過半時回報 onTileError（畫面改說明＋〔改用平面圖〕）。

export interface DevTiles {
  /** 網址樣板：{z} {x} {y}，{s} = subdomains 輪流 */
  url: string;
  attribution?: string;
  subdomains?: string;
  max_zoom?: number;
}

export const DEFAULT_DEV_TILES: DevTiles = {
  url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
  attribution: '© OpenStreetMap contributors, tiles © OpenStreetMap France',
  subdomains: 'abc',
  max_zoom: 19,
};

/** 開發底圖的圖磚網址（key = z/x/y；{s} 依 x + y 輪流分流）。 */
export function devTileUrl(key: string, cfg: DevTiles = DEFAULT_DEV_TILES): string {
  const [z, x, y] = key.split('/').map(Number);
  const subs = cfg.subdomains || 'a';
  return cfg.url
    .replace('{s}', subs[(x + y) % subs.length])
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y));
}

const TILE = 256;

/** WGS-84 → 世界像素（zoom z）。 */
export function worldPx(p: LatLng, z: number): { x: number; y: number } {
  const s = TILE * 2 ** z;
  const sin = Math.min(Math.max(Math.sin((p.lat * Math.PI) / 180), -0.9999), 0.9999);
  return { x: ((p.lng + 180) / 360) * s, y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * s };
}

export function fromWorldPx(x: number, y: number, z: number): LatLng {
  const s = TILE * 2 ** z;
  const lng = (x / s) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / s;
  return { lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))), lng };
}

function devAdapter(el: HTMLElement, center: LatLng, zoom: number, cfg: DevTiles): MapAdapter {
  const maxZoom = cfg.max_zoom || 19;
  el.innerHTML = '';
  el.classList.add('kmap-dev');
  const tiles = document.createElement('div');
  tiles.className = 'kmap-tiles';
  const pane = document.createElement('div');
  pane.className = 'kmap-pane';
  const attr = document.createElement('div');
  attr.className = 'kmap-attr';
  attr.textContent = `${cfg.attribution ?? ''} · dev basemap`.replace(/^ · /, '');
  const zoomBox = document.createElement('div');
  zoomBox.className = 'kmap-zoom';
  zoomBox.innerHTML = '<button type="button" aria-label="+">+</button><button type="button" aria-label="-">−</button>';
  el.append(tiles, pane, attr, zoomBox);
  let z = Math.round(zoom);
  let c = worldPx(center, z);
  let clickCb: ((p: LatLng) => void) | null = null;
  let zoomCb: ((z: number) => void) | null = null;
  let tileErrCb: (() => void) | null = null;
  let tileErr = 0;
  let tileOk = 0;
  const topLeft = () => ({ x: c.x - el.clientWidth / 2, y: c.y - el.clientHeight / 2 });
  const layer = new OverlayLayer(
    {
      project: (p) => {
        const w = worldPx(p, z);
        const tl = topLeft();
        return { x: w.x - tl.x, y: w.y - tl.y };
      },
      pane: () => pane,
    },
    (at) => setView(at, Math.min(z + 3, maxZoom)),
  );
  function render() {
    const tl = topLeft();
    const n = 2 ** z;
    const x0 = Math.floor(tl.x / TILE);
    const y0 = Math.floor(tl.y / TILE);
    const x1 = Math.floor((tl.x + el.clientWidth) / TILE);
    const y1 = Math.floor((tl.y + el.clientHeight) / TILE);
    const want = new Set<string>();
    for (let x = x0; x <= x1; x++)
      for (let y = y0; y <= y1; y++) {
        if (y < 0 || y >= n) continue;
        const key = `${z}/${((x % n) + n) % n}/${y}`;
        want.add(key + '@' + x);
        let img = tiles.querySelector<HTMLImageElement>(`img[data-k="${key}@${x}"]`);
        if (!img) {
          img = document.createElement('img');
          img.dataset.k = key + '@' + x;
          img.onerror = () => {
            img!.style.visibility = 'hidden';
            tileErr++;
            if (tileErr >= 3 && tileErr > tileOk) tileErrCb?.();
          };
          img.onload = () => void tileOk++;
          img.referrerPolicy = 'strict-origin-when-cross-origin';
          img.src = devTileUrl(key, cfg);
          img.alt = '';
          img.draggable = false;
          tiles.appendChild(img);
        }
        img.style.left = `${x * TILE - tl.x}px`;
        img.style.top = `${y * TILE - tl.y}px`;
      }
    tiles.querySelectorAll<HTMLImageElement>('img').forEach((i) => !want.has(i.dataset.k!) && i.remove());
    layer.draw();
  }
  function setView(at: LatLng, zz: number) {
    z = Math.min(Math.max(Math.round(zz), 2), maxZoom);
    c = worldPx(at, z);
    tiles.innerHTML = '';
    render();
    zoomCb?.(z);
  }
  function zoomAt(delta: number, px?: { x: number; y: number }) {
    const tl = topLeft();
    const anchor = px ? fromWorldPx(tl.x + px.x, tl.y + px.y, z) : fromWorldPx(c.x, c.y, z);
    const nz = Math.min(Math.max(z + delta, 2), maxZoom);
    if (nz === z) return;
    const before = px ?? { x: el.clientWidth / 2, y: el.clientHeight / 2 };
    z = nz;
    const a = worldPx(anchor, z);
    c = { x: a.x - before.x + el.clientWidth / 2, y: a.y - before.y + el.clientHeight / 2 };
    tiles.innerHTML = '';
    render();
    zoomCb?.(z);
  }
  let drag: { x: number; y: number; moved: boolean } | null = null;
  el.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('.kmap-marker, .kmap-zoom')) return;
    drag = { x: e.clientX, y: e.clientY, moved: false };
    el.setPointerCapture(e.pointerId);
  });
  el.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
    if (!drag.moved) return;
    c = { x: c.x - dx, y: c.y - dy };
    drag.x = e.clientX;
    drag.y = e.clientY;
    render();
  });
  el.addEventListener('pointerup', (e) => {
    const d = drag;
    drag = null;
    if (!d || d.moved || !clickCb) return;
    const r = el.getBoundingClientRect();
    const tl = topLeft();
    clickCb(fromWorldPx(tl.x + e.clientX - r.left, tl.y + e.clientY - r.top, z));
  });
  el.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1 : -1, { x: e.clientX - r.left, y: e.clientY - r.top });
    },
    { passive: false },
  );
  const [zin, zout] = zoomBox.querySelectorAll('button');
  zin.onclick = () => zoomAt(1);
  zout.onclick = () => zoomAt(-1);
  const ro = new ResizeObserver(() => render());
  ro.observe(el);
  render();
  return {
    provider: 'dev',
    onZoom: (cb) => {
      zoomCb = cb;
    },
    onTileError: (cb) => {
      tileErrCb = cb;
    },
    setView,
    fit: (pts) => {
      if (!pts.length) return;
      if (pts.length === 1) return setView(pts[0], 18);
      const lats = pts.map((p) => p.lat);
      const lngs = pts.map((p) => p.lng);
      const ctr = { lat: (Math.min(...lats) + Math.max(...lats)) / 2, lng: (Math.min(...lngs) + Math.max(...lngs)) / 2 };
      let zz = 19;
      for (; zz > 2; zz--) {
        const a = worldPx({ lat: Math.max(...lats), lng: Math.min(...lngs) }, zz);
        const b = worldPx({ lat: Math.min(...lats), lng: Math.max(...lngs) }, zz);
        if (b.x - a.x < el.clientWidth - 96 && b.y - a.y < el.clientHeight - 96) break;
      }
      setView(ctr, zz);
    },
    getZoom: () => z,
    getCenter: () => fromWorldPx(c.x, c.y, z),
    setLayers: (l) => layer.set(l),
    onClick: (cb) => {
      clickCb = cb;
    },
    destroy: () => {
      ro.disconnect();
      el.innerHTML = '';
      el.classList.remove('kmap-dev');
    },
  };
}

/** 供應商選擇（§5.2.2）：使用者選的 > 國家碼（CN → 百度）> 預設；選到的沒 key 就換另一家；兩家都沒 key：dev / test 用開發底圖，prod 回 null。 */
export function pickProvider(keys: MapKeys, preferred: MapProvider, env = 'prod'): MapEngine | null {
  if (hasKey(keys, preferred)) return preferred;
  const other: MapProvider = preferred === 'google' ? 'baidu' : 'google';
  if (hasKey(keys, other)) return other;
  return hasDevFeatures(env) ? 'dev' : null;
}
